import * as analytics from 'analytics/Analytics';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {
  MolbioFeature,
  ProcessesFeature,
  RegistryFeature,
  V2RequestsFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import * as FolderConstants from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PolymerSubtype} from 'constants/MolbioConstants';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {openImportProteinModal} from 'molbio/aa-sequences/ImportAaSequenceModal';
import {BulkAssemblyMethod, FragmentProductionMethod} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {
  handleNewCloningBulkAssemblyClick,
  handleNewConcatenationBulkAssemblyClick,
} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {
  BIOENTITY_CREATION_LOCATIONS,
  DNA_SEQUENCE_IMPORT_FLOW,
  OLIGO_CREATION_METHODS,
} from 'molbio/MolBioConstants';
import {trackClickOnCreateNewOligo} from 'molbio/SequenceAnalyticsHelper';
import {
  createEntry,
  createProtein,
  createProtocol,
  createSequence,
  openBatchSequenceImportModal,
  openBulkAssembleDNA,
  openBulkCreateInventoryFilesFlow,
  openBulkReimportProteins,
  openBulkReimportSequences,
  openBulkTranslateDNAtoAA,
  openBulkUpdateBioentitiesModal,
  openCreateFileModal,
  openCreateMoleculeModal,
  openCreateOligoModal,
  openCRISPRGuideModal,
  openImportMoleculesFromSpreadsheet,
  openImportOligoModal,
  openImportProteinsFromSpreadsheet,
  openImportSequenceForHRTemplateModal,
  openImportSequences,
  openImportSequencesFromSpreadsheet,
  openNewFolder,
  showCreateEntryFromTemplateModal,
  showCreateEntryModal,
} from 'nav/CreateDropdownActions';
import CreateRequestV2SubmissionModal from 'processes/requests_v2/modals/CreateRequestV2SubmissionModal';
import {SearchParams} from 'search/SearchConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import runModal from 'ui-library/runModal';

export interface CreateDropdownOption {
  name: string;
  value: string;
  disabled?: boolean;
  icon?: BenchlingIconName;
  isHeader?: boolean;
  className?: string;
  starrable?: boolean;
  handleClick?: () => void;
  menu?: Array<CreateDropdownOption>;
  dataTestAttrs?: DataTestAttrs;
}

function trackCreateSequence(importFlow: string): void {
  analytics.track('sequence', 'open_create_sequence_modal', {
    product: ANALYTICS_MODULE.MOLBIO,
    createdFrom: BIOENTITY_CREATION_LOCATIONS.PROJECT,
    initialImportFlow: importFlow,
  });
}

function _getSequenceSubmenu(folder: FolderConstants.StringIDFolder): Array<CreateDropdownOption> {
  const maybeOptions = [
    isFeatureEnabled({itemType: ModelTypeConstants.SEQUENCE, feature: StandardFeature.CREATE}) && {
      name: 'New DNA / RNA sequence',
      value: 'dna-sequence-new-dna-sequence',
      icon: ModelTypeConstants.SEQUENCE,
      handleClick: () => {
        createSequence(folder.id);
        trackCreateSequence(DNA_SEQUENCE_IMPORT_FLOW.RAW);
      },
    },
    isFeatureEnabled({itemType: ModelTypeConstants.SEQUENCE, feature: StandardFeature.IMPORT}) && {
      name: 'Import DNA / RNA sequences',
      value: 'dna-sequence-import-dna-sequences',
      icon: 'import',
      handleClick: () => {
        openImportSequences(folder.id);
        trackCreateSequence(DNA_SEQUENCE_IMPORT_FLOW.FILE);
      },
    },
    isFeatureEnabled({itemType: ModelTypeConstants.SEQUENCE, feature: StandardFeature.IMPORT}) && {
      name: 'Import DNA / RNA sequences from spreadsheet',
      value: 'sequence-import-sequences-from-spreadsheet',
      icon: 'import',
      handleClick: () => {
        openImportSequencesFromSpreadsheet(folder.id);
      },
    },
    isFeatureEnabled({itemType: ModelTypeConstants.SEQUENCE, feature: StandardFeature.IMPORT}) && {
      name: 'Reimport DNA / RNA sequences',
      value: 'sequence-reimport-sequences',
      icon: 'import',
      handleClick: () => {
        openBulkReimportSequences(folder.id);
      },
    },
    isFeatureEnabled({itemType: ModelTypeConstants.SEQUENCE, feature: StandardFeature.IMPORT}) && {
      name: 'Bulk import DNA / RNA from genome',
      value: 'dna-sequence-bulk-import-dna-from-genome',
      icon: 'import',
      handleClick: () => {
        openBatchSequenceImportModal(folder.id);
      },
    },
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) && {
      name: 'Assemble DNA sequences by cloning',
      value: 'dna-sequence-bulk-assemble-dna',
      icon: ModelTypeConstants.BULK_ASSEMBLY,
      handleClick: () => {
        openBulkAssembleDNA();
      },
    },
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) && {
      name: 'Assemble DNA / RNA sequences by concatenation',
      value: 'sequence-assemble-by-concatenation',
      icon: ModelTypeConstants.BULK_ASSEMBLY,
      handleClick: () => {
        handleNewConcatenationBulkAssemblyClick(
          {
            initialMethodData: {
              type: BulkAssemblyMethod.CONCATENATION,
              parameters: {
                polymerSubtype: PolymerSubtype.dna_sequence,
                defaultFragmentProductionMethod: FragmentProductionMethod.SLICE,
                applyDefaultNullTerminalPhosphate: true,
              },
            },
            initialFolderId: folder.id,
          },
          ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL
        );
      },
    },
  ];

  return maybeOptions.filter((o) => Boolean(o)) as Array<CreateDropdownOption>;
}

function _getOligoSubmenu(folder: FolderConstants.StringIDFolder): Array<CreateDropdownOption> {
  const maybeOptions = [
    isFeatureEnabled({itemType: ModelTypeConstants.SEQUENCE, feature: StandardFeature.CREATE}) && {
      name: 'New DNA / RNA oligo',
      value: 'oligo-new-oligo',
      icon: ModelTypeConstants.DNA_OLIGO,
      handleClick: () => {
        trackClickOnCreateNewOligo(
          BIOENTITY_CREATION_LOCATIONS.PROJECT_SIDEBAR_CREATE_BUTTON,
          OLIGO_CREATION_METHODS.MODAL
        );
        openCreateOligoModal(folder.id);
      },
    },
    isFeatureEnabled({itemType: ModelTypeConstants.SEQUENCE, feature: StandardFeature.IMPORT}) && {
      name: 'Import DNA / RNA oligos from spreadsheet',
      value: 'oligo-import-oligos',
      icon: 'import',
      handleClick: () => {
        trackClickOnCreateNewOligo(
          BIOENTITY_CREATION_LOCATIONS.PROJECT_SIDEBAR_CREATE_BUTTON,
          OLIGO_CREATION_METHODS.SPREADSHEET
        );
        openImportOligoModal(folder.id);
      },
    },
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) && {
      name: 'Assemble DNA / RNA oligos by concatenation',
      value: 'oligo-assemble-by-concatenation',
      icon: ModelTypeConstants.BULK_ASSEMBLY,
      handleClick: () => {
        handleNewConcatenationBulkAssemblyClick(
          {
            initialMethodData: {
              type: BulkAssemblyMethod.CONCATENATION,
              parameters: {
                polymerSubtype: PolymerSubtype.dna_oligo,
                defaultFragmentProductionMethod: FragmentProductionMethod.SLICE,
                applyDefaultNullTerminalPhosphate: true,
              },
            },
            initialFolderId: folder.id,
          },
          ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL
        );
      },
    },
  ];

  return maybeOptions.filter((o) => Boolean(o)) as Array<CreateDropdownOption>;
}

function _getMoleculeSubmenu(folder: FolderConstants.StringIDFolder): Array<CreateDropdownOption> {
  const maybeOptions = [
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && {
      name: 'New molecule',
      value: 'molecule-new-molecule',
      icon: ModelTypeConstants.MOLECULE,
      handleClick: () => {
        openCreateMoleculeModal(folder.id);
      },
    },
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && {
      name: 'Import molecules',
      value: 'molecule-import-molecules-spreadsheet',
      icon: 'import',
      handleClick: () => {
        openImportMoleculesFromSpreadsheet(folder.id);
      },
    },
  ];

  return maybeOptions.filter((o) => Boolean(o)) as Array<CreateDropdownOption>;
}

function _getProteinSubmenu(folder: FolderConstants.StringIDFolder): Array<CreateDropdownOption> {
  const maybeOptions = [
    isFeatureEnabled({itemType: ModelTypeConstants.PROTEIN, feature: StandardFeature.CREATE}) && {
      name: 'New AA sequence',
      value: 'aa-sequence-new-aa-sequence',
      icon: ModelTypeConstants.PROTEIN,
      handleClick: () => {
        createProtein(folder.id);
      },
    },
    isFeatureEnabled({itemType: ModelTypeConstants.PROTEIN, feature: StandardFeature.IMPORT}) && {
      name: 'Import AA sequences',
      value: 'aa-sequence-import-aa-sequences',
      icon: 'import',
      handleClick: () => {
        analytics.track('protein', 'open_import_aa_modal', {
          product: ANALYTICS_MODULE.MOLBIO,
          createdFrom: BIOENTITY_CREATION_LOCATIONS.PROJECT,
        });
        openImportProteinModal({initialFolderId: folder.id});
      },
    },
    isFeatureEnabled({itemType: ModelTypeConstants.PROTEIN, feature: StandardFeature.IMPORT}) && {
      name: 'Import AA sequences from spreadsheet',
      value: 'aa-sequence-import-aa-sequences-from-spreadsheet',
      icon: 'import',
      handleClick: () => {
        openImportProteinsFromSpreadsheet(folder.id);
      },
    },
    isFeatureEnabled({itemType: ModelTypeConstants.PROTEIN, feature: StandardFeature.IMPORT}) && {
      name: 'Reimport AA sequences',
      value: 'aa-sequence-reimport-aa-sequences',
      icon: 'import',
      handleClick: () => {
        openBulkReimportProteins(folder.id);
      },
    },
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) && {
      name: 'Assemble AA sequences by concatenation',
      value: 'aa-assemble-by-concatenation',
      icon: ModelTypeConstants.BULK_ASSEMBLY,
      handleClick: () => {
        handleNewConcatenationBulkAssemblyClick(
          {
            initialMethodData: {
              type: BulkAssemblyMethod.CONCATENATION,
              parameters: {
                polymerSubtype: PolymerSubtype.aa_sequence,
                defaultFragmentProductionMethod: FragmentProductionMethod.SLICE,
                applyDefaultNullTerminalPhosphate: true,
              },
            },
            initialFolderId: folder.id,
          },
          ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL
        );
      },
    },
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) && {
      name: 'Bulk translate DNA to AA',
      value: 'aa-sequence-bulk-translate-dna-to-aa',
      icon: ModelTypeConstants.PROTEIN,
      handleClick: () => {
        openBulkTranslateDNAtoAA();
      },
    },
  ];

  return maybeOptions.filter((o) => Boolean(o)) as Array<CreateDropdownOption>;
}

function _getEntrySubmenu(folder: FolderConstants.StringIDFolder): Array<CreateDropdownOption> {
  const createEntryMenu: Array<CreateDropdownOption> = [
    {
      name: 'Blank entry',
      value: 'entry-blank-entry',
      icon: FolderItemIcon.Entry,
      handleClick: () => {
        // TODO(BNCH-38468): Remove feature flag after rollout and discontinue lagacy modal
        if (
          BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW &&
          BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL
        ) {
          showCreateEntryModal({initialFolderId: folder.id});
        } else {
          createEntry(folder.id);
        }
      },
    },
    {
      name: 'Entry from template',
      value: 'entry-entry-from-template',
      icon: 'template',
      handleClick: () => {
        showCreateEntryFromTemplateModal({initialFolderId: folder.id});
      },
    },
  ];
  return createEntryMenu;
}

function _getAssemblySubmenu(folder: FolderConstants.StringIDFolder): Array<CreateDropdownOption> {
  const maybeOptions = [
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) && {
      name: 'Assemble DNA sequences by cloning',
      value: 'cloning-bulk-assembly',
      icon: ModelTypeConstants.BULK_ASSEMBLY,
      handleClick: () => {
        handleNewCloningBulkAssemblyClick({}, ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL);
      },
    },
    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}) && {
      name: 'Assemble sequences and oligos by concatenation',
      value: 'concatenation-bulk-assembly',
      icon: ModelTypeConstants.BULK_ASSEMBLY,
      handleClick: () => {
        handleNewConcatenationBulkAssemblyClick({}, ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL);
      },
    },
  ];
  return maybeOptions.filter((o) => Boolean(o)) as Array<CreateDropdownOption>;
}

export function getFolderCreateDropdownOptions({
  antibodySchemaOptions,
  bfiSchemaOptions,
  complexPolymerSchemaConfigs,
  complexPolymerSchemaOptions,
  folder,
  handleOpenFolder,
  mixtureSchemaOptions,
  permissions,
  processSchemaOptions,
  studySchemaOptions,
}: {
  antibodySchemaOptions: Array<CreateDropdownOption>;
  bfiSchemaOptions: Array<CreateDropdownOption>;
  complexPolymerSchemaConfigs: Array<ComplexPolymerSchemaConfigGraphQL>;
  complexPolymerSchemaOptions: Array<CreateDropdownOption>;
  folder: FolderConstants.StringIDFolder;
  handleOpenFolder: (folder: FolderConstants.StringIDFolder, searchParams?: SearchParams) => void;
  mixtureSchemaOptions: Array<CreateDropdownOption>;
  permissions: Permissions | null;
  processSchemaOptions: Array<CreateDropdownOption>;
  studySchemaOptions: Array<CreateDropdownOption>;
}): Array<CreateDropdownOption> {
  const showCreateFolder = permissions && permissions.subfolderCreatable;
  const showCreateFolderItems = permissions && permissions.appendable;
  const showCreateProtocol = BenchlingConfig.ENABLE_PROTOCOLS;
  const showCreateEntities = isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE});
  const showCreateMixture =
    showCreateEntities && isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES});
  const showCreateProcess = isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE});
  const showCreateStudy = BenchlingConfig.STUDIES_ENABLE;
  const showCreateRequestV2Submission = isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_CREATE});
  const showCreateComplexPolymer = BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES;
  const showCreateAntibody = BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENTITIES;

  const createFolderSubmenu = showCreateFolder
    ? [
        {
          name: 'Folder',
          value: 'folder',
          icon: ModelTypeConstants.FOLDER,
          handleClick: () => {
            openNewFolder(folder.id, FolderConstants.Type.ARBITRARY, handleOpenFolder);
          },
        } satisfies CreateDropdownOption,
      ]
    : [];

  if (!showCreateFolderItems) {
    return createFolderSubmenu;
  }

  const moreOptions: Array<CreateDropdownOption> = [
    ...(showCreateEntities
      ? [
          {
            name: 'Import entities',
            value: 'more-import-entities',
            icon: 'table',
            handleClick: () => {
              openBulkCreateInventoryFilesFlow(folder, complexPolymerSchemaConfigs, false);
            },
            dataTestAttrs: {component: 'CreateMenu', element: 'importEntities'},
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(showCreateEntities
      ? [
          {
            name: 'Update entities',
            value: 'more-update-entities',
            icon: 'table',
            handleClick: () => {
              openBulkUpdateBioentitiesModal(folder, complexPolymerSchemaConfigs);
            },
          } satisfies CreateDropdownOption,
        ]
      : []),
  ];

  const sequenceSubmenu = _getSequenceSubmenu(folder);
  const oligoSubmenu = _getOligoSubmenu(folder);
  const proteinSubmenu = _getProteinSubmenu(folder);
  const moleculeSubmenu = _getMoleculeSubmenu(folder);
  const entrySubmenu = _getEntrySubmenu(folder);
  const assemblySubmenu = _getAssemblySubmenu(folder);
  const options: Array<CreateDropdownOption> = [
    ...createFolderSubmenu,
    {
      name: 'File',
      value: 'file',
      icon: ModelTypeConstants.FILE,
      handleClick: () => {
        openCreateFileModal(folder.id);
      },
    } satisfies CreateDropdownOption,
    {
      name: BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION ? 'Entry' : 'Entry from Template',
      value: 'entry',
      icon: FolderItemIcon.Entry,
      ...(BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION
        ? {
            menu: entrySubmenu,
            handleClick: () => {},
          }
        : {
            handleClick: () => {
              showCreateEntryFromTemplateModal({initialFolderId: folder.id});
            },
          }),
    } satisfies CreateDropdownOption,
    ...(showCreateProtocol
      ? [
          {
            name: 'Protocol',
            value: 'protocol',
            icon: ModelTypeConstants.PROTOCOL,
            handleClick: () => {
              createProtocol(folder.id);
            },
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(sequenceSubmenu.length > 0
      ? [
          {
            name: 'DNA / RNA sequence',
            value: 'dna-sequence',
            icon: 'helix',
            handleClick: () => {},
            menu: sequenceSubmenu,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(proteinSubmenu.length > 0
      ? [
          {
            name: 'AA sequence',
            value: 'aa-sequence',
            icon: 'protein',
            handleClick: () => {},
            menu: proteinSubmenu,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(oligoSubmenu.length > 0
      ? [
          {
            name: 'Oligo',
            value: 'oligo',
            icon: 'oligo',
            handleClick: () => {},
            menu: oligoSubmenu,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(assemblySubmenu.length > 0 && BenchlingConfig.MOLBIO_REDESIGNED_CLONING_FLOW
      ? [
          {
            name: 'Assembly',
            value: 'bulk-assembly',
            icon: ModelTypeConstants.BULK_ASSEMBLY,
            handleClick: () => {},
            menu: assemblySubmenu,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(showCreateComplexPolymer
      ? [
          {
            name: 'Oligo duplex or oligo conjugate',
            value: 'complex-polymer',
            icon: 'network-wired',
            handleClick: () => {},
            menu: complexPolymerSchemaOptions,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(showCreateAntibody
      ? [
          {
            name: 'Antibody',
            value: 'antibody',
            icon: 'antibody',
            handleClick: () => {},
            menu: antibodySchemaOptions,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE})
      ? [
          {
            name: 'CRISPR',
            value: 'crispr',
            icon: 'crispr',
            handleClick: () => {},
            menu: [
              {
                name: 'CRISPR guides',
                value: 'crispr-crispr-guides',
                icon: 'crispr',
                handleClick: () => {
                  openCRISPRGuideModal(folder.id);
                },
              } satisfies CreateDropdownOption,
              {
                name: 'HR template',
                value: 'crispr-hr-template',
                icon: 'hr-template',
                handleClick: () => {
                  openImportSequenceForHRTemplateModal(folder.id);
                },
              } satisfies CreateDropdownOption,
            ],
          } satisfies CreateDropdownOption,
        ]
      : []),
    // TODO (Steven): populate correct options with GraphQL
    ...(showCreateEntities && bfiSchemaOptions.length > 0
      ? [
          {
            name: 'Entity from schema',
            value: 'entity-from-schema',
            icon: FolderItemIcon.BasicFolderItem,
            handleClick: () => {},
            menu: bfiSchemaOptions,
            dataTestAttrs: {component: 'CreateMenu', element: 'entityFromSchema'},
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(showCreateMixture && mixtureSchemaOptions.length > 0
      ? [
          {
            name: 'Mixture',
            value: 'mixture',
            icon: FolderItemIcon.Mixture,
            handleClick: () => {},
            menu: mixtureSchemaOptions,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(moleculeSubmenu.length > 0
      ? [
          {
            name: 'Molecule',
            value: 'molecule',
            icon: FolderItemIcon.Molecule,
            handleClick: () => {},
            menu: moleculeSubmenu,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(showCreateRequestV2Submission
      ? [
          {
            name: 'Request',
            value: 'requestV2Submission',
            icon: ModelTypeConstants.REQUEST_V2_SUBMISSION,
            handleClick: () => {
              runModal(CreateRequestV2SubmissionModal, {
                initialFolderId: folder.id,
                actionOrigin: ActionOrigin.SIDEPANEL_FOLDER_CREATE,
              });
            },
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(showCreateStudy
      ? [
          {
            name: 'Study',
            value: 'study',
            icon: ModelTypeConstants.STUDY,
            menu: studySchemaOptions,
            handleClick: () => {},
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(showCreateProcess
      ? [
          {
            name: 'Workflow tasks',
            value: 'process',
            icon: ModelTypeConstants.PROCESS,
            handleClick: () => {},
            menu: processSchemaOptions,
          } satisfies CreateDropdownOption,
        ]
      : []),
    ...(moreOptions.length > 0
      ? [
          {
            name: 'More',
            value: 'more',
            handleClick: () => {},
            menu: moreOptions,
            dataTestAttrs: {component: 'CreateMenu', element: 'more'},
          } satisfies CreateDropdownOption,
        ]
      : []),
  ];

  return options;
}
